import LayersIcon from "@mui/icons-material/Layers";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { MigrationStatus } from "../../api/system-info";
import { formatDateTime } from "../../utils/date-formatting";
import { NavbarBadge } from "./navbar-badge";

const MAX_LISTED = 8;

/** "core.1211_workflowticket_ci" -> "1211", other apps keep their app name. */
const shortLabel = (fullName: string): string => {
	const separator = fullName.indexOf(".");
	const app = fullName.slice(0, separator);
	const number = fullName.slice(separator + 1).split("_")[0];
	return app === "core" ? number : `${app} ${number}`;
};

const MigrationList = ({ title, names }: { title: string; names: string[] }) => (
	<Box sx={{ mt: 1 }} data-component="migration-badge-list">
		<Box sx={{ fontWeight: 600 }}>{title}</Box>
		{names.slice(0, MAX_LISTED).map((name) => (
			<Box key={name} sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
				{name}
			</Box>
		))}
		{names.length > MAX_LISTED && <Box>+{names.length - MAX_LISTED} more</Box>}
	</Box>
);

/**
 * Last migration applied on this system, red when the database carries
 * migrations this code does not have, amber when the code has migrations that
 * were never run here.
 */
export const MigrationBadge = ({ migrations }: { migrations: MigrationStatus }) => {
	const theme = useTheme();
	if (!migrations.last_applied) return null;

	const hasUnknown = migrations.unknown.length > 0;
	const hasUnapplied = migrations.unapplied.length > 0;
	const bgcolor = hasUnknown
		? theme.palette.error.main
		: hasUnapplied
			? theme.palette.warning.main
			: undefined;

	return (
		<Tooltip
			arrow
			title={
				<Box sx={{ fontSize: "0.8rem" }}>
					<Box sx={{ fontFamily: "monospace" }}>
						{migrations.last_applied}
					</Box>
					<Box>Applied {formatDateTime(migrations.last_applied_at)}</Box>
					{hasUnknown && (
						<MigrationList
							title="Applied here but missing from this code:"
							names={migrations.unknown}
						/>
					)}
					{hasUnapplied && (
						<MigrationList
							title="In this code but not run here:"
							names={migrations.unapplied}
						/>
					)}
				</Box>
			}
		>
			<NavbarBadge dataComponent="migration-status" bgcolor={bgcolor}>
				<LayersIcon sx={{ fontSize: "1rem" }} />
				{shortLabel(migrations.last_applied)}
			</NavbarBadge>
		</Tooltip>
	);
};
